import {useEffect, useState} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {connect} from 'react-redux'

import {getImageOfSize} from '../../utils'

import {pause, play, playNewTrack} from '../../actions' 

import PlayIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'

import TrackCardButtons from './TrackCardButtons'

import {Image} from '../generics'



export const styles = {
  card: css`
    position: relative;
    width: 100%;
    height: 330px;
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    transition: 0.3s;
    box-shadow:
      rgba(0, 0, 0, 0.3) 0px 1px 2px 0px,
      rgba(0, 0, 0, 0.15) 0px 1px 3px 1px;

    @media (max-width: 600px) {
      width: 100%;
      max-width: 100vw;
      height: auto;
      padding: 10px 20px;
      display: flex;
      flex-direction: row;
      border-radius: 0;
      box-shadow: none;
      gap: 10px;
    }
  `,
  imageContainer: css`
    width: 100%;
    height: 200px;
    border-radius: 6px 6px 0 0;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    @media (max-width: 600px) {
      width: 80px;
      height: 80px;
      border-radius: 6px;
      box-shadow:
        rgba(0, 0, 0, 0.3) 0px 1px 2px 0px,
        rgba(0, 0, 0, 0.15) 0px 1px 3px 1px;
    }
  `,
  infoContainer: css`
    padding: 12px 12px 0 12px;
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    h3 {
      width: 100%;
      max-width: 100%;
      font-weight: 600;
      font-size: 1rem;
      line-height: 1.2;
      padding-right: 5px;
      margin-bottom: 1px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    p {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 1rem;
      max-width: 100%;
      color: #7b7b7b;
      font-weight: 500;
    }

    @media (max-width: 600px) {
      padding: 12px;
      justify-content: center;
    }
  `,
  buttons: css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 8px 10px;

    @media (max-width: 600px) {
      width: auto;
      margin: 0;
    }
  `,
  overlay: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    cursor: pointer;
    transition: opacity 0.3s;

    &:hover {
      opacity: 1;
    }
  `,
  iconContainer: css`
    background-color: black;
    opacity: 0.8;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  icon: css`
    color: white;
    font-size: 50px;
  `
}



const TrackCard = props => {

  const isExtraSmallScreen = useMediaQuery(theme => theme.breakpoints.only('xs'))

  const { 
    track,
    play, 
    pause,
    isPlaying, 
    playNewTrack,
    playerTrackId,
    spotifyAccessToken,
    // spotifyAccessTokenExpiration
  } = props

  const {name, composer, permission, image, launchAt} = track.attributes

  const [now, setNow] = useState(Date.now())

  const imageURL = getImageOfSize(track.attributes, 'small') || image


  useEffect(() => {
    if (!launchAt) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [launchAt])


  const launchTime  = launchAt ? new Date(launchAt).getTime() : null
  const isLaunched  = !launchTime || launchTime <= now
  const countdownMs = launchTime && launchTime > now ? launchTime - now : 0
  const countdown   = countdownMs > 0 ? formatCountdown(countdownMs) : null



  function playTrack() {
    if (!isLaunched) return
    // If the player is already loaded with the track, then play it
    if (playerTrackId === track.id) { 
      play()
      return
    } 

    playNewTrack(track, track.id, 'track')
  }


  function handleClick(e) {
    e.stopPropagation()
    isPlaying ? pause() : playTrack()
  }


  function formatCountdown(ms) {
    if (ms <= 0) return null
    const totalSeconds = Math.floor(ms / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2,'0')}:${seconds.toString().padStart(2,'0')}`
    }
    return `${minutes}:${seconds.toString().padStart(2,'0')}`
  }


  return (
    <div css={styles.card}>
      <div css={styles.imageContainer} onClick={isLaunched ? handleClick : undefined} style={!isLaunched ? {cursor: 'not-allowed'} : undefined}>
        <Image src={imageURL} alt='track' />
          {!isExtraSmallScreen &&
            <div css={styles.overlay} style={isLaunched ? {} : {opacity: 1, pointerEvents: 'none', background: 'rgba(0,0,0,0.6)'}}>
              <div css={styles.iconContainer}>
                {isLaunched
                  ? (isPlaying ? <PauseIcon css={styles.icon} /> : <PlayIcon css={styles.icon} />)
                  : (
                    <span style={{ color: '#fff', fontSize: 18, textAlign: 'center', lineHeight: 1.2 }}>
                      {countdown
                        ? <>Launching in<br/>{countdown}</>
                        : <>Launching soon</>
                      }
                    </span>
                  )
                }
              </div>
            </div>
          }
      </div>

      {/* Countdown banner for small screens */}
      {!isLaunched && isExtraSmallScreen &&
        <div
          css={css`
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.82);
            color: #fff;
            font-weight: 500;
            font-size: 1rem;
            z-index: 10;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            pointer-events: all;
            user-select: none;
            padding: 0 16px;
          `}
        >
          <div>
            {countdown
              ? <>
                  <strong>{name}</strong><br/>
                  Launching in <span style={{fontVariantNumeric:'tabular-nums'}}>{countdown}</span>
                </>
              : <>
                  <strong>{name}</strong><br/>
                  Launching soon
                </>
            }
          </div>
        </div>
      }

      <div css={styles.infoContainer} onClick={handleClick}>
        <h3>{name}</h3>
        <p>{composer}</p>
      </div>


      <div css={styles.buttons}>
        <TrackCardButtons
          track={track}
          name={name}
          imageURL={imageURL}
          permission={permission}
          spotifyAccessToken={spotifyAccessToken}
        />
      </div>
    </div>
  )
}



const mapStateToProps = (state, props) => {
  const id = props.track.id

  const player = state.player
  const playerTrack = player.queue[player.position]

  return {
    track: props.track,
    isPlaying: player.isPlayRequested && playerTrack?.id === id,
    playerTrackId: playerTrack?.id,
    spotifyAccessToken: state.user.spotify.accessToken,
    spotifyAccessTokenExpiration: state.user.spotify.expires
  }
}



const actions = {play, pause, playNewTrack}


export default connect(mapStateToProps, actions)(TrackCard)





